import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';

const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getLegalPageData } = useStructuredData();

  const pageTitle = t('privacyPolicy.title');
  const pageDescription = i18n.language === 'en'
    ? 'Learn how Alerta Legal collects, uses, and protects your personal information. Our comprehensive privacy policy explains our data practices and your rights.'
    : 'Conoce cómo Alerta Legal recopila, usa y protege tu información personal. Nuestra política de privacidad integral explica nuestras prácticas de datos y tus derechos.';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';
  const canonicalUrl = `${siteUrl}${t('routes.privacyPolicy')}`;
  const structuredData = getLegalPageData(pageTitle, pageDescription, t('routes.privacyPolicy'), 'PrivacyPolicy');

  const effectiveDate = i18n.language === 'en' ? 'July 14, 2025' : '14 de julio de 2025';

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Privacy Policy - AlertaLegal" fallbackDescription="Learn how Alerta Legal collects, uses, and protects your personal information.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            tabIndex={-1}
            id="main-heading"
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            {t('privacyPolicy.title')}
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 6, 
              textAlign: 'center', 
              color: 'text.secondary', 
              fontStyle: 'italic',
              fontSize: '1.1rem'
            }}
          >
            {t('privacyPolicy.effectiveDate', { date: effectiveDate })}
          </Typography>

          <Card sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* Introduction */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.introduction.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.introduction.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Data Collection */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.dataCollection.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.dataCollection.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataCollection.personalInfo')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataCollection.technicalInfo')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataCollection.communicationInfo')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Data Usage */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.dataUsage.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.dataUsage.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataUsage.monitoring')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataUsage.notifications')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataUsage.serviceImprovement')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataUsage.support')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Data Retention */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.dataRetention.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.dataRetention.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Data Sharing */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.dataSharing.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.dataSharing.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataSharing.legalRequirements')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataSharing.serviceProviders')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.dataSharing.businessTransfer')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Data Security */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.dataSecurity.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.dataSecurity.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Your Rights */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.yourRights.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.yourRights.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.yourRights.access')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.yourRights.correction')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.yourRights.deletion')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.yourRights.portability')}
                  </Typography>
                  <Typography component="li" variant="body1" paragraph>
                    {t('privacyPolicy.sections.yourRights.objection')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Third Party Services */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.thirdPartyServices.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.thirdPartyServices.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Cookies and Tracking */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.cookiesAndTracking.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.cookiesAndTracking.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Children's Privacy */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.childrenPrivacy.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.childrenPrivacy.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Policy Changes */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.policyChanges.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.policyChanges.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Contact Information */}
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('privacyPolicy.sections.contact.title')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.contact.content')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.contact.email')}
                </Typography>
                <Typography variant="body1" paragraph>
                  {t('privacyPolicy.sections.contact.address')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default PrivacyPolicy;